import { Fab, Icon, styled, useMediaQuery } from '@mui/material';
import React, { ReactNode, useRef, useState } from 'react';

interface CarouselProps {
  children: ReactNode;
  padding?: number;
}

export const Carousel: React.FC<CarouselProps> = ({ children, padding = 30 }) => {
  const isAboveSmall = useMediaQuery('(min-width:481px)');
  const tileWrapperRef = useRef<HTMLDivElement>(null);
  const [currentTile, setCurrentTile] = useState(0);

  const handleScroll = () => {
    if (tileWrapperRef.current) {
      const scrollLeft = tileWrapperRef.current.scrollLeft;
      const childNodes = Array.from(tileWrapperRef.current.children) as HTMLElement[];
      const containerWidth = tileWrapperRef.current.offsetWidth;

      const closestIndex = childNodes.reduce(
        (closest, child, index) => {
          const childCenter = child.offsetLeft + child.offsetWidth / 2;
          const containerCenter = scrollLeft + containerWidth / 2;
          const distance = Math.abs(childCenter - containerCenter);
          return distance < closest.distance ? { index, distance } : closest;
        },
        { index: 0, distance: Infinity }
      ).index;

      setCurrentTile(closestIndex);
    }
  };

  const tileCount = React.Children.count(children);
  const showPrevButton = isAboveSmall && currentTile > 0;
  const showNextButton = isAboveSmall && currentTile < tileCount - 1;

  const buttonClickHandlerFactory = (dir: 'prev' | 'next') => () => {
    if (tileWrapperRef.current) {
      let newTile;

      if (dir === 'prev') {
        newTile = Math.max(currentTile - 1, 0);
      } else {
        newTile = Math.min(currentTile + 1, tileCount - 1);
      }

      const childNodes = Array.from(tileWrapperRef.current.children) as HTMLElement[];
      const targetTile = childNodes[newTile];
      const scrollLeft = targetTile.offsetLeft - tileWrapperRef.current.offsetLeft;
      tileWrapperRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const gradientWidth = 30 + padding;

  return (
    <CarouselContainer gradientWidth={gradientWidth}>
      {showPrevButton && (
        <StyledFab
          color='primary'
          size='small'
          onClick={buttonClickHandlerFactory('prev')}
          left
        >
          <Icon>arrow_backward</Icon>
        </StyledFab>
      )}

      <TileScrollContainer
        padding={padding}
        ref={tileWrapperRef}
        onScroll={handleScroll}
      >
        {children}
      </TileScrollContainer>

      {showNextButton && (
        <StyledFab
          color='primary'
          size='small'
          onClick={buttonClickHandlerFactory('next')}
          right
        >
          <Icon>arrow_forward</Icon>
        </StyledFab>
      )}

      <CarouselDotContainer>
        {Array.from({ length: tileCount }).map((_, index) => (
          <CarouselDot
            key={index}
            isActive={index === currentTile}
          />
        ))}
      </CarouselDotContainer>
    </CarouselContainer>
  );
};

const CarouselContainer = styled('div')<{ gradientWidth: number }>`
  position: relative;
  max-width: 100vw;

  &::before,
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    width: ${({ gradientWidth }) => `${gradientWidth}px`};
    pointer-events: none;
    z-index: 1;
  }

  &:before {
    left: 0;
    background: linear-gradient(to left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  }

  &:after {
    right: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  }
`;

const TileScrollContainer = styled('div')<{ padding: number }>`
  padding-inline: ${({ padding }) => `${padding}px`};
  display: flex;
  flex-direction: row;
  overflow-x: scroll;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  gap: 12px;

  > * {
    min-width: 90%;
    scroll-snap-align: center;
  }
`;

const CarouselDotContainer = styled('div')`
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-top: 24px;
`;

const CarouselDot = styled('div')<{ isActive: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ isActive }) => (isActive ? 'var(--mui-palette-primary-main)' : 'var(--mui-palette-grey-600)')};
  transition: background-color 0.3s ease;
`;

const StyledFab = styled(Fab)<{ left?: boolean; right?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  ${({ left }) => left && 'left: 10px;'}
  ${({ right }) => right && 'right: 10px;'}
`;
